import React, { useState, useRef, useEffect } from 'react';

/**
 * TypeaheadSelect — searchable dropdown replacement for <select>
 *
 * Props:
 *   options   : [{ value, label, sub }]  — sub is optional secondary line
 *   value     : current selected value (id)
 *   onChange  : (value) => void
 *   placeholder: string
 *   disabled  : bool
 */
export default function TypeaheadSelect({ options = [], value, onChange, placeholder = 'Type to search…', disabled = false }) {
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef  = useRef(null);
  const listRef   = useRef(null);
  const wrapRef   = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
        setActive(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.sub && o.sub.toLowerCase().includes(query.toLowerCase())))
    : options;

  const handleSelect = (opt) => {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
    setActive(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    setActive(-1);
    if (!e.target.value) onChange('');
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && active >= 0 && filtered[active]) {
      e.preventDefault();
      handleSelect(filtered[active]);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (active >= 0 && listRef.current) {
      const item = listRef.current.children[active];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  const displayValue = open ? query : (selected ? selected.label : query);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          className="form-control"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected ? '' : placeholder}
          disabled={disabled}
          autoComplete="off"
          style={{ paddingRight: 32 }}
        />
        {/* clear button */}
        {(selected || query) && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
              padding: '0 2px', lineHeight: 1, fontSize: '1rem',
            }}
            tabIndex={-1}
            aria-label="Clear"
          >×</button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <ul
          ref={listRef}
          style={{
            position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0,
            background: 'var(--surface-1, #fff)', border: '1px solid var(--border-color, #d1d5db)',
            borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            maxHeight: 240, overflowY: 'auto', margin: '2px 0', padding: 0, listStyle: 'none',
          }}
        >
          {filtered.map((opt, i) => (
            <li
              key={opt.value}
              onMouseDown={() => handleSelect(opt)}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                background: i === active ? 'var(--primary-light, #eff6ff)' : 'transparent',
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none',
              }}
              onMouseEnter={() => setActive(i)}
            >
              <div style={{ fontSize: '0.875rem', fontWeight: String(opt.value) === String(value) ? 600 : 400 }}>
                {opt.label}
              </div>
              {opt.sub && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: 1 }}>
                  {opt.sub}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && query && filtered.length === 0 && (
        <div style={{
          position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0,
          background: 'var(--surface-1, #fff)', border: '1px solid var(--border-color, #d1d5db)',
          borderRadius: 6, padding: '10px 12px', fontSize: '0.85rem',
          color: 'var(--text-muted)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          No results for "{query}"
        </div>
      )}
    </div>
  );
}
